import {
  Component,
  input,
  model,
  output,
  signal,
  computed,
  effect,
  viewChild,
  inject,
  ElementRef,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ZagIcon } from '../../icon/icon';

export interface SearchScopeOption<T extends string = string> {
  /** The value emitted when this scope is selected */
  id: T;
  /** Already-translated display label — the consumer owns translation */
  label: string;
  /** Optional leading zagIcon name */
  icon?: string;
}

@Component({
  selector: 'zag-search',
  imports: [FormsModule, TranslateModule, ZagIcon],
  templateUrl: './card-search.component.html',
  styleUrls: ['./card-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'onEscape($event)',
  },
})
export class CardSearchComponent implements OnDestroy {
  /**
   * Placeholder text for search input (translation key)
   */
  placeholder = input<string>('Search...');

  /**
   * Debounce time in milliseconds
   */
  debounceMs = input<number>(300);

  /**
   * Minimum characters before emitting search term
   */
  minChars = input<number>(1);

  /**
   * Current search term (input)
   */
  searchTerm = input<string>('');

  /**
   * Scope options rendered as a badge inside the input. Empty disables the scope UI entirely.
   */
  readonly scopes = input<SearchScopeOption[]>([]);

  /**
   * Selected scope id (two-way). Emits `scopeChange`.
   */
  readonly scope = model<string | undefined>(undefined);

  /**
   * Already-translated accessible label for the scope badge (e.g. "Change scope")
   */
  readonly scopeAriaLabel = input<string>('');

  /**
   * Search term change event (output)
   */
  searchTermChange = output<string>();

  /**
   * Internal immediate search value
   */
  immediateSearchValue = signal<string>('');

  readonly hasScope = computed<boolean>(() => this.scopes().length > 0);

  /**
   * Selected scope, falling back to the first option when `scope()` matches none
   */
  readonly activeScope = computed<SearchScopeOption | undefined>(() => {
    const options = this.scopes();
    return options.find(option => option.id === this.scope()) ?? options[0];
  });

  readonly scopeMenuOpen = signal<boolean>(false);

  private readonly scopeBadge = viewChild<ElementRef<HTMLButtonElement>>('scopeBadge');

  private readonly hostRef = inject(ElementRef<HTMLElement>);

  /**
   * Debounce timer
   */
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Update model with debounced immediate value
    effect(() => {
      const term = this.immediateSearchValue();
      this.scheduleEmit(term);
    });
  }

  /**
   * Handle input change
   */
  onInputChange(value: string): void {
    this.immediateSearchValue.set(value);
  }

  /**
   * Clear search term
   */
  clearSearch(): void {
    this.immediateSearchValue.set('');
    this.searchTermChange.emit('');
  }

  toggleScopeMenu(): void {
    this.scopeMenuOpen.update(open => !open);
  }

  isActiveScope(option: SearchScopeOption): boolean {
    return option.id === this.activeScope()?.id;
  }

  selectScope(option: SearchScopeOption): void {
    this.scope.set(option.id);
    this.scopeMenuOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.scopeMenuOpen()) return;
    if (this.hostRef.nativeElement.contains(event.target as Node)) return;
    this.scopeMenuOpen.set(false);
  }

  onEscape(event: Event): void {
    if (!this.scopeMenuOpen()) return;
    event.stopPropagation();
    this.scopeMenuOpen.set(false);
    this.scopeBadge()?.nativeElement.focus();
  }

  /**
   * Schedule update of search term with debounce
   */
  private scheduleEmit(term: string): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.searchTermChange.emit(term);
    }, this.debounceMs());
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
